/* crm-meetings.jsx — Meeting management components for Phase 4
   Provides: ScheduleMeetingModal, MeetingCard, MeetingTimeline,
             MeetingDetailPanel, MeetingsCalendarView
   All components self-register on window. */
import { Modal } from '../../shared/ui/index.jsx';

const { useState, useEffect, useMemo, useRef, useCallback } = React;

/* ── helpers ── */
const _fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const _fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

const _toLocalDatetime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
};

const STATUS_CONFIG = {
  SCHEDULED:   { tone: 'blue',  label: 'Scheduled' },
  COMPLETED:   { tone: 'green', label: 'Completed' },
  CANCELLED:   { tone: 'gray',  label: 'Cancelled' },
  RESCHEDULED: { tone: 'amber', label: 'Rescheduled' },
};

const DURATION_OPTIONS = [15, 30, 45, 60, 90];

/* Shared status-update helper — avoids duplicate function bodies across components */
const _setMtgStatus = async (wsId, mtgId, status, successMsg, toast, reload) => {
  try {
    await window.__nx_api?.meetings?.update(wsId, mtgId, { status });
    toast({ tone: 'success', icon: 'Check', title: successMsg });
    if (typeof reload === 'function') await reload();
  } catch (e) {
    toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to update meeting' });
  }
};

/* ══════════════════════════════════════════════════════════════
   SCHEDULE MEETING MODAL
   ══════════════════════════════════════════════════════════════ */
const ScheduleMeetingModal = ({ record, onClose, onScheduled }) => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const toast = useToast();

  const [form, setForm] = useState({
    title: '', scheduledAt: '', duration: 30, location: '',
    agenda: '', participants: '',
  });
  const [saving, setSaving] = useState(false);

  const upd = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const _buildPayload = () => {
    const payload = {
      title: form.title.trim(),
      scheduledAt: form.scheduledAt ? new Date(form.scheduledAt).toISOString() : undefined,
      duration: Number(form.duration),
      location: form.location.trim() || undefined,
      agenda: form.agenda.trim() || undefined,
      participants: form.participants.split(/[\n,]+/).map(p => p.trim()).filter(Boolean),
    };
    if (record?.id) {
      const t = (record._type || '').toLowerCase();
      if (t === 'person' || t === 'people') payload.personId = record.id;
      else if (t === 'company' || t === 'companies') payload.companyId = record.id;
      else if (t === 'deal' || t === 'deals') payload.dealId = record.id;
    }
    return payload;
  };

  const handleSchedule = async () => {
    if (!form.title.trim()) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: 'Title is required' });
      return;
    }
    if (!form.scheduledAt) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: 'Date and time are required' });
      return;
    }
    setSaving(true);
    try {
      const mtg = await window.__nx_api?.meetings?.create(wsId, _buildPayload());
      toast({ tone: 'success', icon: 'Check', title: 'Meeting scheduled' });
      if (typeof onScheduled === 'function') onScheduled(mtg);
      onClose();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to schedule meeting' });
    } finally { setSaving(false); }
  };

  return (
    <Modal
      title="Schedule Meeting"
      icon="Calendar"
      onClose={onClose}
      width={520}
      footer={
        <>
          <Btn variant="ghost" size="sm" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn variant="primary" size="sm" onClick={handleSchedule} disabled={saving}>
            <NexusIcon name="Calendar" size={12} style={{ marginRight: 4 }} />
            {saving ? 'Scheduling…' : 'Schedule Meeting'}
          </Btn>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label htmlFor="smm-title" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Title *</label>
          <input id="smm-title" className="nx-input" style={{ width: '100%' }} value={form.title} onChange={e => upd('title', e.target.value)} placeholder="Meeting title" autoFocus />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 2 }}>
            <label htmlFor="smm-scheduledAt" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Date &amp; Time *</label>
            <input id="smm-scheduledAt" className="nx-input" style={{ width: '100%' }} type="datetime-local" value={form.scheduledAt} onChange={e => upd('scheduledAt', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="smm-duration" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Duration</label>
            <select id="smm-duration" className="nx-select" style={{ width: '100%' }} value={form.duration} onChange={e => upd('duration', e.target.value)}>
              {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d} min</option>)}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="smm-location" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Location</label>
          <input id="smm-location" className="nx-input" style={{ width: '100%' }} value={form.location} onChange={e => upd('location', e.target.value)} placeholder="e.g. Zoom link, office address" />
        </div>

        <div>
          <label htmlFor="smm-participants" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Participants</label>
          <textarea
            id="smm-participants"
            className="nx-input"
            rows={2}
            style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
            value={form.participants}
            onChange={e => upd('participants', e.target.value)}
            placeholder="Names or emails, one per line or comma-separated"
          />
        </div>

        <div>
          <label htmlFor="smm-agenda" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Agenda</label>
          <textarea
            id="smm-agenda"
            className="nx-input"
            rows={4}
            style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
            value={form.agenda}
            onChange={e => upd('agenda', e.target.value)}
            placeholder="Meeting agenda…"
          />
        </div>
      </div>
    </Modal>
  );
};
window.ScheduleMeetingModal = ScheduleMeetingModal;

/* ══════════════════════════════════════════════════════════════
   MEETING CARD
   ══════════════════════════════════════════════════════════════ */
const MeetingCard = ({ meeting, onEdit, onComplete, onCancel }) => {
  const cfg = STATUS_CONFIG[meeting?.status] || STATUS_CONFIG.SCHEDULED;
  const participantCount = Array.isArray(meeting?.participants) ? meeting.participants.length : 0;

  return (
    <div style={{ border: '1px solid var(--nx-border)', borderRadius: 10, background: 'var(--nx-surface)', overflow: 'hidden' }}>
      {/* Card header */}
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--nx-border)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <NexusIcon name="Calendar" size={16} style={{ color: 'var(--nx-accent)' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>{meeting?.title || 'Untitled Meeting'}</span>
            <Pill tone={cfg.tone}>{cfg.label}</Pill>
          </div>
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
            {_fmtDateTime(meeting?.scheduledAt)}
            {meeting?.duration && <span style={{ marginLeft: 8 }}>{meeting.duration} min</span>}
          </div>
        </div>
        {typeof onEdit === 'function' && meeting?.status !== 'CANCELLED' && meeting?.status !== 'COMPLETED' && (
          <IconBtn icon="Edit" title="Edit meeting" onClick={onEdit} />
        )}
      </div>

      {/* Card body */}
      <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {meeting?.location && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--nx-text-muted)' }}>
            <NexusIcon name="MapPin" size={12} style={{ flexShrink: 0 }} />
            <span>{meeting.location}</span>
          </div>
        )}
        {meeting?.agenda && (
          <div style={{ fontSize: 12, color: 'var(--nx-text)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {meeting.agenda}
          </div>
        )}
        {participantCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--nx-text-muted)' }}>
            <NexusIcon name="Users" size={12} style={{ flexShrink: 0 }} />
            <span>{participantCount} participant{participantCount !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      {(meeting?.status === 'SCHEDULED' || meeting?.status === 'RESCHEDULED') && (
        <div style={{ padding: '8px 14px', borderTop: '1px solid var(--nx-border)', display: 'flex', gap: 8 }}>
          {typeof onComplete === 'function' && (
            <Btn variant="ghost" size="sm" onClick={onComplete} style={{ color: 'var(--nx-success)' }}>
              <NexusIcon name="CheckCircle" size={12} style={{ marginRight: 4 }} />
              Complete
            </Btn>
          )}
          {typeof onCancel === 'function' && (
            <Btn variant="ghost" size="sm" onClick={onCancel} style={{ color: 'var(--nx-danger)' }}>
              <NexusIcon name="XCircle" size={12} style={{ marginRight: 4 }} />
              Cancel
            </Btn>
          )}
        </div>
      )}
    </div>
  );
};
window.MeetingCard = MeetingCard;

/* ══════════════════════════════════════════════════════════════
   MEETING TIMELINE
   ══════════════════════════════════════════════════════════════ */
const MeetingTimeline = ({ record, type }) => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const toast = useToast();

  const [meetings, setMeetings]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editMeeting, setEditMeeting] = useState(null);

  const _buildParams = useCallback(() => {
    if (!record?.id) return {};
    const t = (type || record._type || '').toLowerCase();
    if (t === 'person' || t === 'people') return { personId: record.id };
    if (t === 'company' || t === 'companies') return { companyId: record.id };
    if (t === 'deal' || t === 'deals') return { dealId: record.id };
    return null;
  }, [record, type]);

  const load = useCallback(async () => {
    if (!wsId || !record?.id) return;
    const params = _buildParams();
    if (!params || Object.keys(params).length === 0) {
      setMeetings([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await window.__nx_api?.meetings?.list(wsId, params);
      const arr = Array.isArray(res) ? res : (res?.data ?? []);
      setMeetings(arr.sort((a, b) => new Date(b.scheduledAt || 0) - new Date(a.scheduledAt || 0)));
    } catch { setMeetings([]); }
    finally { setLoading(false); }
  }, [wsId, record, _buildParams]);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async (mtg) => {
    try {
      await window.__nx_api?.meetings?.update(wsId, mtg.id, { status: 'COMPLETED' });
      toast({ tone: 'success', icon: 'Check', title: 'Meeting marked complete' });
      await load();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to update meeting' });
    }
  };

  const handleCancel = async (mtg) => {
    try {
      await window.__nx_api?.meetings?.update(wsId, mtg.id, { status: 'CANCELLED' });
      toast({ tone: 'success', icon: 'Check', title: 'Meeting cancelled' });
      await load();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to cancel meeting' });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Btn variant="primary" size="sm" onClick={() => setShowModal(true)}>
          <NexusIcon name="Plus" size={12} style={{ marginRight: 4 }} />
          Schedule Meeting
        </Btn>
      </div>

      {loading ? (
        <div style={{ color: 'var(--nx-text-muted)', fontSize: 12, padding: '8px 0' }}>Loading meetings…</div>
      ) : meetings.length === 0 ? (
        <div className="nx-empty" style={{ padding: 'var(--space-5) 0' }}>
          <NexusIcon name="Calendar" size={20} />
          <span>No meetings scheduled yet.</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {meetings.map(mtg => (
            <MeetingCard
              key={mtg.id}
              meeting={mtg}
              onEdit={() => setEditMeeting(mtg)}
              onComplete={() => handleComplete(mtg)}
              onCancel={() => handleCancel(mtg)}
            />
          ))}
        </div>
      )}

      {showModal && (
        <ScheduleMeetingModal
          record={record}
          onClose={() => setShowModal(false)}
          onScheduled={load}
        />
      )}

      {editMeeting && (
        <MeetingDetailPanel
          meeting={editMeeting}
          onUpdate={async (data) => {
            try {
              await window.__nx_api?.meetings?.update(wsId, editMeeting.id, data);
              toast({ tone: 'success', icon: 'Check', title: 'Meeting updated' });
              setEditMeeting(null);
              await load();
            } catch (e) {
              toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to update meeting' });
            }
          }}
          onClose={() => setEditMeeting(null)}
        />
      )}
    </div>
  );
};
window.MeetingTimeline = MeetingTimeline;

/* ══════════════════════════════════════════════════════════════
   MEETING DETAIL PANEL
   ══════════════════════════════════════════════════════════════ */
const MeetingDetailPanel = ({ meeting, onUpdate, onClose }) => {
  const toast = useToast();
  const [form, setForm] = useState({
    title:        meeting?.title || '',
    scheduledAt:  _toLocalDatetime(meeting?.scheduledAt),
    duration:     meeting?.duration || 30,
    location:     meeting?.location || '',
    agenda:       meeting?.agenda || '',
    notes:        meeting?.notes || '',
    outcomes:     meeting?.outcomes || '',
    nextActions:  meeting?.nextActions || '',
  });
  const [editSection, setEditSection] = useState(null);
  const [saving, setSaving] = useState(false);

  const upd = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async (section) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.scheduledAt) payload.scheduledAt = new Date(payload.scheduledAt).toISOString();
      payload.duration = Number(payload.duration);
      await onUpdate(payload);
      setEditSection(null);
    } catch { /* parent handles toast */ }
    finally { setSaving(false); }
  };

  const _field = (label, key, multiline = false) => {
    const isEditing = editSection === key;
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', flex: 1 }}>{label}</span>
          {!isEditing && (
            <IconBtn icon="Edit" size={11} title={`Edit ${label}`} onClick={() => setEditSection(key)} />
          )}
        </div>
        {isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {multiline ? (
              <textarea
                className="nx-input"
                rows={4}
                style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
                value={form[key]}
                onChange={e => upd(key, e.target.value)}
                autoFocus
              />
            ) : key === 'scheduledAt' ? (
              <input className="nx-input" style={{ width: '100%' }} type="datetime-local" value={form[key]} onChange={e => upd(key, e.target.value)} autoFocus />
            ) : key === 'duration' ? (
              <select className="nx-select" style={{ width: '100%' }} value={form[key]} onChange={e => upd(key, e.target.value)}>
                {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d} min</option>)}
              </select>
            ) : (
              <input className="nx-input" style={{ width: '100%' }} value={form[key]} onChange={e => upd(key, e.target.value)} autoFocus />
            )}
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" size="sm" onClick={() => setEditSection(null)}>Cancel</Btn>
              <Btn variant="primary" size="sm" onClick={() => handleSave(key)} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: form[key] ? 'var(--nx-text)' : 'var(--nx-text-muted)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {key === 'scheduledAt' ? _fmtDateTime(meeting?.scheduledAt)
             : key === 'duration' ? `${form[key]} min`
             : form[key] || <span style={{ fontStyle: 'italic' }}>Not set</span>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div style={{ background: 'var(--nx-surface)', borderRadius: 12, width: 540, maxWidth: '95vw', boxShadow: 'var(--nx-shadow-lg)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--nx-border)' }}>
          <NexusIcon name="Calendar" size={16} style={{ color: 'var(--nx-accent)' }} />
          <span style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>Meeting Details</span>
          <Pill tone={(STATUS_CONFIG[meeting?.status] || STATUS_CONFIG.SCHEDULED).tone}>
            {(STATUS_CONFIG[meeting?.status] || STATUS_CONFIG.SCHEDULED).label}
          </Pill>
          <IconBtn icon="X" onClick={onClose} title="Close" />
        </div>

        <div style={{ padding: '16px 18px', overflowY: 'auto', flex: 1 }}>
          {_field('Title', 'title')}
          {_field('Date & Time', 'scheduledAt')}
          {_field('Duration', 'duration')}
          {_field('Location', 'location')}
          {_field('Agenda', 'agenda', true)}
          {_field('Notes', 'notes', true)}
          {_field('Outcomes', 'outcomes', true)}
          {_field('Next Actions', 'nextActions', true)}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 18px', borderTop: '1px solid var(--nx-border)' }}>
          <Btn variant="ghost" size="sm" onClick={onClose}>Close</Btn>
        </div>
      </div>
    </div>
  );
};
window.MeetingDetailPanel = MeetingDetailPanel;

/* ══════════════════════════════════════════════════════════════
   MEETINGS CALENDAR VIEW
   ══════════════════════════════════════════════════════════════ */
const MeetingsCalendarView = () => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const toast = useToast();

  const [meetings, setMeetings]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [showModal, setShowModal]   = useState(false);
  const [editMeeting, setEditMeeting] = useState(null);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const res = await window.__nx_api?.meetings?.list(wsId);
      const arr = Array.isArray(res) ? res : (res?.data ?? []);
      setMeetings(arr.sort((a, b) => new Date(a.scheduledAt || 0) - new Date(b.scheduledAt || 0)));
    } catch { setMeetings([]); }
    finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const _getGroup = (iso) => {
    if (!iso) return 'Unscheduled';
    const d = new Date(iso);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 86400000);
    const weekEnd  = new Date(today.getTime() + 7 * 86400000);
    const mt = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (mt.getTime() === today.getTime()) return 'Today';
    if (mt.getTime() === tomorrow.getTime()) return 'Tomorrow';
    if (mt < weekEnd) return 'This Week';
    return 'Later';
  };

  const grouped = useMemo(() => {
    const ORDER = ['Today', 'Tomorrow', 'This Week', 'Later', 'Unscheduled'];
    const map = new Map();
    meetings.forEach(m => {
      if (m.status === 'CANCELLED') return;
      const g = _getGroup(m.scheduledAt);
      const arr = map.get(g) || [];
      arr.push(m);
      map.set(g, arr);
    });
    return ORDER.filter(k => map.has(k)).map(k => ({ label: k, items: map.get(k) }));
  }, [meetings]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--nx-border)' }}>
        <NexusIcon name="Calendar" size={16} style={{ color: 'var(--nx-accent)' }} />
        <span style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>Meetings</span>
        <Btn variant="primary" size="sm" onClick={() => setShowModal(true)}>
          <NexusIcon name="Plus" size={12} style={{ marginRight: 4 }} />
          Schedule Meeting
        </Btn>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        {loading ? (
          <div style={{ color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading meetings…</div>
        ) : grouped.length === 0 ? (
          <div className="nx-empty" style={{ padding: 'var(--space-6) 0' }}>
            <NexusIcon name="Calendar" size={24} />
            <span>No upcoming meetings.</span>
          </div>
        ) : (
          grouped.map(({ label, items }) => (
            <div key={label} style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                {label === 'Today' && <NexusIcon name="Sun" size={12} style={{ color: '#F59E0B' }} />}
                {label === 'Tomorrow' && <NexusIcon name="Sunrise" size={12} style={{ color: '#F59E0B' }} />}
                {label}
                <span style={{ fontWeight: 400, color: 'var(--nx-text-muted)', background: 'var(--nx-border)', borderRadius: 10, padding: '1px 7px', fontSize: 10 }}>{items.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {items.map(mtg => (
                  <MeetingCard
                    key={mtg.id}
                    meeting={mtg}
                    onEdit={() => setEditMeeting(mtg)}
                    onComplete={() => _setMtgStatus(wsId, mtg.id, 'COMPLETED', 'Meeting completed', toast, load)}
                    onCancel={() => _setMtgStatus(wsId, mtg.id, 'CANCELLED', 'Meeting cancelled', toast, load)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <ScheduleMeetingModal
          record={null}
          onClose={() => setShowModal(false)}
          onScheduled={load}
        />
      )}

      {editMeeting && (
        <MeetingDetailPanel
          meeting={editMeeting}
          onUpdate={async (data) => {
            try {
              await window.__nx_api?.meetings?.update(wsId, editMeeting.id, data);
              toast({ tone: 'success', icon: 'Check', title: 'Meeting updated' });
              setEditMeeting(null);
              await load();
            } catch (e) {
              toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to update meeting' });
            }
          }}
          onClose={() => setEditMeeting(null)}
        />
      )}
    </div>
  );
};
window.MeetingsCalendarView = MeetingsCalendarView;
